// lib/webSearchCommand.ts
import type { UiCommandDefinition, WebSearchRequestBody } from '#types/types';

const WEB_SEARCH_FRESHNESS_SET = new Set<WebSearchRequestBody['freshness']>(['oneDay', 'oneWeek', 'oneMonth', 'oneYear']);

// Keep Dealer's legacy 503 fallback; Slides distinguishes unavailable from failed.
export function createWebSearchHandler(
  command: string,
  label: string,
  { unavailableOn503 = false, retryScope = 'session' }: { unavailableOn503?: boolean; retryScope?: string } = {},
): UiCommandDefinition['handler'] {
  return async (params, kernel, options) => {
    if (!kernel.current) {
      throw new Error(`${command}: Kernel is not available.`);
    }
    const query = params?.query;
    if (typeof query !== 'string' || query.trim().length === 0) {
      throw new Error(`${command}: "query" must be a non-empty string.`);
    }

    const trimmedQuery = query.trim();
    const requestBody: WebSearchRequestBody = { query: trimmedQuery };
    const searchStartedAt = Date.now();

    const freshness = params?.freshness;
    if (freshness !== undefined) {
      if (typeof freshness !== 'string') {
        throw new Error(`${command}: "freshness" must be a string if provided.`);
      }

      const typedFreshness = freshness as string;
      if (!WEB_SEARCH_FRESHNESS_SET.has(typedFreshness as WebSearchRequestBody['freshness'])) {
        throw new Error(`${command}: "freshness" must be one of oneDay, oneWeek, oneMonth, or oneYear.`);
      }

      requestBody.freshness = typedFreshness as WebSearchRequestBody['freshness'];
    }

    if (typeof params?.summary === 'boolean') {
      requestBody.summary = params.summary;
    } else if (params?.summary !== undefined) {
      throw new Error(`${command}: "summary" must be a boolean if provided.`);
    }

    const response = await fetch('/api/ai/web-search', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestBody),
      signal: options?.abortSignal
    });

    if (!response.ok) {
      let errorMessage = 'Web search failed.';
      let errorCode: string | null = null;
      try {
        const errorPayload = await response.json();
        if (typeof errorPayload?.message === 'string' && errorPayload.message.length > 0) {
          errorMessage = errorPayload.message;
        }
        if (typeof errorPayload?.code === 'string') errorCode = errorPayload.code;
      } catch { /* Keep the HTTP fallback when the error body is not JSON. */ }
      if (errorCode === 'WEB_SEARCH_NOT_CONFIGURED' || (unavailableOn503 && response.status === 503)) {
        params.results = {
          provider: 'none',
          query: trimmedQuery,
          results: [],
          notice:
            `Web search is unavailable: no search provider is configured in this environment. Do not retry ${command} this ${retryScope}. Answer from internal data and, if the missing information matters, tell the user web search is currently unavailable so results may be incomplete.`,
        };
        kernel.current.logUserAction?.(`${label} Web Search Unconfigured`, {});
        return;
      }
      throw new Error(errorMessage);
    }

    const results = await response.json();
    const elapsedMs = Date.now() - searchStartedAt;
    const rawResults = Array.isArray((results as { results?: unknown[] })?.results)
      ? ((results as { results: Array<{ url?: string }> }).results)
      : [];
    const hosts = Array.from(
      new Set(
        rawResults
          .map((entry) => {
            try {
              return new URL(String(entry?.url ?? '')).hostname.replace(/^www\./, '');
            } catch {
              return '';
            }
          })
          .filter(Boolean),
      ),
    ).slice(0, 4);
    (results as Record<string, unknown>).progress = {
      elapsedMs,
      elapsedLabel: `${(elapsedMs / 1000).toFixed(1)}s`,
      sourceCount: rawResults.length,
      hosts,
      outcome: rawResults.length === 0 ? 'no sources found' : `${rawResults.length} sources`,
    };
    params.results = results;

    kernel.current.logUserAction?.(`${label} Web Search Executed`, {
      has_freshness: typeof requestBody.freshness === 'string',
      requested_summary: typeof requestBody.summary === 'boolean' ? requestBody.summary : false,
    });
  };
}
